/**
 * Layout.tsx — Responsive layout wrapper.
 * Desktop: sidebar + topbar.
 * Mobile: bottom nav shell + page content via Outlet.
 * Deteksi via useIsMobile (width + touch + UA) — tahan "Desktop site" di HP.
 * Sama-sama pakai <Outlet />, routing TIDAK berubah.
 */
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/use-auth';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { useSwipeTabs } from '../mobile/hooks/useSwipeTabs';
import { MOBILE_NAV, brand, copy, desktop } from '../../brand';

// ═══════════════════════════════════════════════════════════════════
// MOBILE — Indopoin × Nevi desk (view-only, Executive Glass)
// ═══════════════════════════════════════════════════════════════════

function MobileNavIcon({ icon, isActive }: { icon: string; isActive: boolean }) {
  const color = isActive ? 'var(--eg-accent, var(--accent-primary))' : 'currentColor';
  const size = 20;

  if (icon === 'grid') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
      </svg>
    );
  }
  if (icon === 'chart') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
      </svg>
    );
  }
  if (icon === 'nevi') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }
  if (icon === 'wallet') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
        <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
        <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
      </svg>
    );
  }
  // gear
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

function MobileShell() {
  const { logout } = useAuth();
  const location = useLocation();
  useSwipeTabs(true);

  const [oled, setOled] = useState(() => {
    try {
      return localStorage.getItem('tc-mob-oled') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onStorage = () => {
      try {
        setOled(localStorage.getItem('tc-mob-oled') === '1');
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('tc-mob-oled', onStorage as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('tc-mob-oled', onStorage as EventListener);
    };
  }, []);

  const itemActive = (to: string) => {
    if (to === '/') return location.pathname === '/' || location.pathname === '';
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  return (
    <div className={`tc-mob tc-mob--eg ${oled ? 'tc-mob--oled' : ''}`}>
      <div className="tc-mob__ambient" aria-hidden />
      <header className="tc-mob__topbar tc-mob__topbar--eg">
        <div className="tc-mob__brand">
          <div className="tc-mob__logo tc-mob__logo--eg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
              <polyline points="16 7 22 7 22 13" />
            </svg>
          </div>
          <div className="tc-mob__brand-text">
            <div className="tc-mob__title">{copy.headerTitle}</div>
            <div className="tc-mob__subtitle">{copy.headerSub}</div>
          </div>
        </div>
        <button type="button" className="tc-mob__logout" onClick={logout} title="Logout">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>
      </header>

      <main className="tc-mob__content">
        <Outlet />
      </main>

      <nav className="tc-mob__bottomnav tc-mob__bottomnav--eg" aria-label={`${brand.company} navigasi`}>
        {MOBILE_NAV.map((item) => {
          const isHome = item.to === '/';
          const active = itemActive(item.to);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={isHome}
              className={({ isActive }) => `tc-mob__navitem ${isActive ? 'tc-mob__navitem--active' : ''}`}
            >
              <div className="tc-mob__navicon">
                <MobileNavIcon icon={item.icon} isActive={active} />
              </div>
              <div className="tc-mob__navlabel">{item.label}</div>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DESKTOP — Sidebar + Topbar (original)
// ═══════════════════════════════════════════════════════════════════

const desktopNavItems = desktop.nav.map((item) => ({ ...item }));

function DesktopNavIcon({ to }: { to: string }) {
  return (
    <svg className="cin-sidebar__icon" width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {to === '/' && (<><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></>)}
      {to === '/market' && (<><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></>)}
      {to === '/kalender' && (<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>)}
      {to === '/sentimen' && (<><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></>)}
      {to === '/likuidasi' && (<><path d="M12 2v20M2 12h20" /><circle cx="12" cy="12" r="3" /></>)}
      {to === '/dcd-explorer' && (<><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /><line x1="12" y1="12" x2="12" y2="16" /><line x1="10" y1="14" x2="14" y2="14" /></>)}
      {to === '/positions' && (<><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></>)}
      {to === '/candidates' && (<><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>)}
      {to === '/history' && (<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>)}
      {to === '/portfolio' && (<><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></>)}
      {to === '/activity' && (<><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></>)}
      {to === '/settings' && (<><circle cx="12" cy="12" r="3" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" /></>)}
      {to === '/dashboard-v3' && (<><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></>)}
    </svg>
  );
}

const desktopSections: { id: string; label: string }[] = [
  { id: 'main', label: desktop.sections.main },
  { id: 'data', label: desktop.sections.data },
  { id: 'nevi', label: desktop.sections.nevi },
  { id: 'config', label: desktop.sections.config },
  { id: 'ref', label: desktop.sections.ref },
];

function DesktopShell() {
  const { user, logout } = useAuth();

  return (
    <div className="app-layout">
      <aside className="app-sidebar cin-sidebar">
        <div className="cin-sidebar__brand">
          <div className="cin-sidebar__logo">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
              <polyline points="16 7 22 7 22 13" />
            </svg>
          </div>
          <div className="cin-sidebar__brand-text">
            <div className="cin-sidebar__title">{desktop.sidebarTitle}</div>
            <div className="cin-sidebar__subtitle">{desktop.sidebarSub}</div>
          </div>
        </div>

        <nav className="cin-sidebar__nav">
          {desktopSections.map((sec) => {
            const items = desktopNavItems.filter((i) => i.section === sec.id);
            if (!items.length) return null;
            return (
              <div key={sec.id}>
                <div className="cin-sidebar__section-label">{sec.label}</div>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) => `cin-sidebar__item ${isActive ? 'cin-sidebar__item--active' : ''}`}
                    title={item.hint}
                  >
                    <DesktopNavIcon to={item.to} />
                    <span className="cin-sidebar__item-text">
                      <span className="cin-sidebar__item-label">{item.label}</span>
                      <span className="cin-sidebar__item-hint">{item.hint}</span>
                    </span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="cin-sidebar__footer">
          <span>{desktop.footer}</span>
        </div>
      </aside>

      <div className="app-right">
        <header className="cin-topbar">
          <div className="cin-topbar__brand">
            <div className="cin-topbar__title">{desktop.topbarTitle}</div>
            <div className="cin-topbar__nevi">
              <span className="cin-topbar__nevi-badge">{desktop.topbarBadge}</span>
              <span className="cin-topbar__nevi-hint">{desktop.topbarHint}</span>
            </div>
          </div>
          <div className="cin-topbar__user">
            <div className="cin-topbar__user-info">
              <div className="cin-topbar__user-name">{user?.username || user?.email || 'User'}</div>
              <div className="cin-topbar__user-role">{user?.role || 'trader'}</div>
            </div>
            <div className="cin-topbar__avatar">
              {(user?.username || user?.email || 'U').charAt(0).toUpperCase()}
            </div>
            <button className="cin-topbar__logout" onClick={logout} title="Logout">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </header>

        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN EXPORT — auto-switch berdasarkan viewport
// ═══════════════════════════════════════════════════════════════════

export function Layout() {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <MobileShell />;
  }

  return <DesktopShell />;
}
